import { useCallback, useEffect, useState } from 'react';
import { issueAbhaCaptcha } from '../services/api/abhaApi';

export const CAPTCHA_DEBOUNCE_MS = 250;

/**
 * Captcha for an ABHA OTP first send (NHA VRFY_ABHA_301).
 *
 * Fetches a challenge only while `active` (the identifier passed its local check), and a new one
 * whenever a binding field changes (debounced), on refresh(), and when it expires. The server binds it
 * to exactly these fields, so `fields` must be the values the OTP request will send.
 *
 * Returns:
 *   status   'idle' | 'loading' | 'ready' | 'disabled' | 'error'
 *   ready    true when the OTP may be sent: the captcha is off (disabled) or an answer is typed
 *   payload  { captchaId, captchaText } for the OTP request, or undefined (captcha off / not ready)
 *   refresh  get a new picture (call after every failed first send: the old one may be spent)
 */
export default function useAbhaCaptcha(purpose, fields, active) {
  const key = active ? JSON.stringify(fields) : '';
  const [challenge, setChallenge] = useState({ status: 'idle' });
  const [text, setText] = useState('');
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    setText('');
    if (!key) {
      setChallenge({ status: 'idle' });
      return undefined;
    }
    let live = true;
    setChallenge({ status: 'loading' });
    const t = setTimeout(async () => {
      try {
        const c = await issueAbhaCaptcha(purpose, JSON.parse(key));
        if (!live) return;
        setChallenge(c.enabled
          ? { status: 'ready', captchaId: c.captchaId, image: c.imagePngBase64, expiresIn: c.expiresIn }
          : { status: 'disabled' });
      } catch (error) {
        if (live) setChallenge({ status: 'error', error });
      }
    }, CAPTCHA_DEBOUNCE_MS);
    return () => { live = false; clearTimeout(t); };
  }, [purpose, key, nonce]);

  // An expired picture can only fail: swap it for a new one in place.
  useEffect(() => {
    if (challenge.status !== 'ready' || !challenge.expiresIn) return undefined;
    const t = setTimeout(() => setNonce((n) => n + 1), challenge.expiresIn * 1000);
    return () => clearTimeout(t);
  }, [challenge]);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  const answer = text.trim();
  const disabled = challenge.status === 'disabled';
  const ready = disabled || (challenge.status === 'ready' && answer.length > 0);
  const payload = challenge.status === 'ready' && answer
    ? { captchaId: challenge.captchaId, captchaText: answer }
    : undefined;

  return { ...challenge, text, setText, refresh, ready, payload, disabled };
}
